import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Rocket } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { totalStock, totalReserved, totalAvailable, getWspRange, isForwarding } from './helpers';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ProductCard = ({
  product,
  user,
  bulk,
  brands,
  vendors,
  expandedProduct, setExpandedProduct,
  openCostHistory,
  openEditProduct,
  deleteProduct,
  openLabelDialog,
  setImageViewProduct, setImageZoom, setGalleryIndex,
  canEditProduct, canDeleteProduct,
}) => {
  const skus = product.skus || [];
  const hasSkus = skus.length > 0;
  const stock = totalStock(skus);
  const wspRange = getWspRange(skus);
  const forwarding = isForwarding(product);
  const imageUrl = product.images?.length > 0 ? `${API_BASE}${product.images[0]}` : null;
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || 'Unknown';
  const getVendorName = (id) => { const v = (vendors || []).find(v => v.id === id); return v ? v.name : ''; };

  return (
    <Card key={product.id} className={`overflow-hidden hover:shadow-lg transition-all duration-200 group relative ${bulk.isSelected(product.id) ? 'ring-2 ring-blue-500' : ''}`} data-testid={`product-card-${product.id}`}>
      {/* Select checkbox + Info badge */}
      <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5">
        <input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(product.id)} onChange={() => bulk.toggleSelect(product.id)} onClick={e => e.stopPropagation()} data-testid={`select-product-${product.id}`} />
        {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'inventory_staff') && (
          <button className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 shadow-sm transition-colors cursor-pointer"
            onClick={e => { e.stopPropagation(); openCostHistory(product); }} data-testid={`info-product-${product.id}`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Info
          </button>
        )}
      </div>
      {/* Image */}
      <div className="relative w-full h-44 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center cursor-pointer"
        onClick={() => { setImageViewProduct(product); setImageZoom(1); setGalleryIndex(0); }}
        data-testid={`product-image-${product.id}`}>
        {imageUrl ? (
          <img src={imageUrl} alt={product.design_code} className="w-full h-full object-cover object-top" />
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-white rounded-xl shadow-sm flex items-center justify-center mb-2">
              <span className="text-xl font-bold text-blue-600">{product.design_code}</span>
            </div>
            <span className="text-xs text-gray-400">No image</span>
          </div>
        )}
        {/* Image count badge */}
        {(product.images?.length || 0) > 1 && (
          <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full font-medium" data-testid={`image-count-${product.id}`}>
            {product.images.length} photos
          </span>
        )}
        {/* Status badge */}
        <Badge className={`absolute top-2 right-2 text-xs ${product.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
          {product.status}
        </Badge>
        {/* Forwarding sample ribbon — orderable but goods not yet received (no PE) */}
        {forwarding && (
          <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow-sm" data-testid={`forwarding-ribbon-${product.id}`}>
            <Rocket className="w-3 h-3" /> Forwarding
          </span>
        )}
        {/* Action buttons */}
        {(canEditProduct || canDeleteProduct) && (
          <div className="absolute inset-x-0 top-2 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
            <div className="flex gap-1">
              {canEditProduct && (
                <button className="bg-white/90 hover:bg-blue-50 rounded-full p-1.5 shadow-sm" onClick={() => openEditProduct(product)} data-testid={`edit-product-${product.id}`}>
                  <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
              )}
              {canDeleteProduct && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="bg-white/90 hover:bg-red-50 rounded-full p-1.5 shadow-sm" data-testid={`delete-product-${product.id}`}>
                      <svg className="w-3.5 h-3.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Product</AlertDialogTitle>
                      <AlertDialogDescription>Delete design "{product.design_code}" ({product.category})?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteProduct(product.id, product.design_code)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-product-${product.id}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Colours below image */}
      {(() => {
        const colors = [...new Set((product.skus || []).map(s => s.color).filter(Boolean))];
        const imgColors = product.image_colors;
        const imgColorVals = Array.isArray(imgColors) ? imgColors : (imgColors && typeof imgColors === 'object' ? Object.values(imgColors).filter(Boolean) : []);
        const allColors = [...new Set([...colors, ...imgColorVals])].slice(0, 6);
        if (allColors.length === 0) return null;
        return (
          <div className="px-3 py-1.5 border-t border-gray-100 flex flex-wrap gap-1" data-testid={`product-colors-${product.id}`}>
            {allColors.map(c => (
              <span key={c} className="inline-flex items-center gap-1 text-[10px] text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded-full border border-gray-200">
                <span className="w-2 h-2 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: c.toLowerCase() }}></span>
                {c}
              </span>
            ))}
            {[...new Set([...colors, ...imgColorVals])].length > 6 && (
              <span className="text-[10px] text-gray-400">+{[...new Set([...colors, ...imgColorVals])].length - 6}</span>
            )}
          </div>
        );
      })()}

      {/* Info */}
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-bold text-gray-900 text-base">{product.design_code}</span>
          <Badge className="bg-blue-50 text-blue-700 text-xs">{getBrandName(product.brand_id)}</Badge>
        </div>
        {product.vendor_id && getVendorName(product.vendor_id) && (
          <div className="text-xs text-gray-500 truncate" data-testid={`product-vendor-${product.id}`}>
            <svg className="w-3 h-3 inline mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            {getVendorName(product.vendor_id)}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">{product.category}</Badge>
          {(product.design_theme || '').split(',').filter(Boolean).map(occ => (
            <Badge key={occ} variant="outline" className="text-xs text-amber-700 border-amber-200">{occ.trim()}</Badge>
          ))}
        </div>

        {/* Stock & Price Info */}
        <div className="pt-2 border-t border-gray-100 space-y-1.5">
          {hasSkus ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Stock</span>
                <span className={`font-semibold ${stock <= 10 ? 'text-red-600' : 'text-green-700'}`}>{stock} pcs</span>
              </div>
              {totalReserved(skus) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Reserved</span>
                  <span className="font-semibold text-amber-600">{totalReserved(skus)} pcs</span>
                </div>
              )}
              {totalReserved(skus) === 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Reserved</span>
                  <span className="text-gray-400">0</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Available</span>
                <span className={`font-semibold ${totalAvailable(skus) <= 10 ? 'text-red-600' : 'text-green-700'}`}>{totalAvailable(skus)} pcs</span>
              </div>
              {wspRange && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">WSP</span>
                  <span className="font-semibold text-gray-900">{wspRange}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">SKUs</span>
                <span className="text-gray-700">{skus.length} variants</span>
              </div>
            </>
          ) : forwarding ? (
            <div className="space-y-1" data-testid={`forwarding-info-${product.id}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1 text-orange-600 font-medium"><Rocket className="w-3.5 h-3.5" /> Forwarding sample</span>
                <span className="font-semibold text-gray-900">Tent. WSP ₹{Number(product.tentative_wsp || 0).toLocaleString('en-IN')}</span>
              </div>
              {totalReserved(skus) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">SO Reserved</span>
                  <span className="font-semibold text-amber-600">{totalReserved(skus)} pcs</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-1">No stock yet — add via Purchase Entry</p>
          )}
        </div>

        {/* Expand for SKU details */}
        <div className="flex gap-0.5">
          {hasSkus && (
            <Button variant="ghost" size="sm" className="flex-1 text-sm mt-1 px-2"
              onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
              data-testid={`expand-product-${product.id}`}>
              {expandedProduct === product.id ? 'Hide SKU' : 'SKU Details'}
              <svg className={`w-3 h-3 ml-0.5 transition-transform ${expandedProduct === product.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </Button>
          )}
          {hasSkus && (
            <Button variant="ghost" size="sm" className="text-sm mt-1 px-2 text-blue-600 hover:bg-blue-50"
              onClick={() => openLabelDialog(product)}
              data-testid={`print-labels-${product.id}`}>
              <svg className="w-3.5 h-3.5 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              Labels
            </Button>
          )}
        </div>
      </CardContent>

      {/* Expanded SKU Table */}
      {expandedProduct === product.id && hasSkus && (
        <div className="border-t overflow-x-auto" data-testid={`sku-table-${product.id}`}>
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 font-medium text-gray-600">SKU</th>
                <th className="text-left p-2 font-medium text-gray-600">Size</th>
                <th className="text-left p-2 font-medium text-gray-600">Color</th>
                <th className="text-right p-2 font-medium text-gray-600">WSP</th>
                <th className="text-right p-2 font-medium text-gray-600">Stock</th>
                <th className="text-right p-2 font-medium text-gray-600">Reserved</th>
                <th className="text-right p-2 font-medium text-gray-600">Available</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((sku, i) => {
                const avail = Math.max(0, (sku.current_stock || 0) - (sku.reserved_stock || 0));
                return (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="p-2 font-mono text-blue-700">{sku.sku_code}</td>
                    <td className="p-2">{sku.size}</td>
                    <td className="p-2">{sku.color}</td>
                    <td className="p-2 text-right">₹{sku.mrp?.toLocaleString('en-IN')}</td>
                    <td className="p-2 text-right"><span className={sku.current_stock <= 5 ? 'text-red-600 font-semibold' : ''}>{sku.current_stock}</span></td>
                    <td className="p-2 text-right"><span className={sku.reserved_stock > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>{sku.reserved_stock || 0}</span></td>
                    <td className="p-2 text-right"><span className={avail <= 5 ? 'text-red-600 font-semibold' : 'text-green-700'}>{avail}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default ProductCard;
